import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FiSearch, FiRefreshCcw } from "react-icons/fi";
import { fetchQueries } from "../../store/slices/adminSlice";
import adminApi from "../../api/adminApi";
import Table from "../../components/Table/Table";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Select from "../../components/Select/Select";
import Button from "../../components/Button/Button";
import "./QueriesList.css";

const QueriesList = () => {
  const dispatch = useDispatch();
  const { queries, isLoading, error } = useSelector((state) => state.admin);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [queryTypeFilter, setQueryTypeFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [batches, setBatches] = useState([]);
  const [doubtSolvers, setDoubtSolvers] = useState([]);

  useEffect(() => {
    loadQueries();
    loadBatches();
    loadDoubtSolvers();
  }, [
    page,
    statusFilter,
    priorityFilter,
    queryTypeFilter,
    batchFilter,
    assignedToFilter,
  ]);

  const loadQueries = async () => {
    const params = {
      page,
      limit: 25,
    };
    if (statusFilter !== "all") {
      params.status = statusFilter;
    }
    if (priorityFilter !== "all") {
      params.priority = priorityFilter;
    }
    if (queryTypeFilter !== "all") {
      params.queryType = queryTypeFilter;
    }
    if (batchFilter !== "all") {
      params.batch = batchFilter;
    }
    if (assignedToFilter !== "all") {
      if (assignedToFilter === "unassigned") {
        // For unassigned, we'll filter on frontend since backend doesn't support null filter
      } else {
        params.assignedTo = assignedToFilter;
      }
    }
    dispatch(fetchQueries(params));
  };

  const loadBatches = async () => {
    try {
      const response = await adminApi.get("/batches", {
        params: { limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const loadDoubtSolvers = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "doubt solver", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setDoubtSolvers(usersList);
    } catch (err) {
      console.error("Failed to load doubt solvers:", err);
    }
  };

  const filteredQueries = useMemo(() => {
    const queriesList = queries?.data || queries?.queries || [];
    if (!Array.isArray(queriesList)) return [];
    const query = searchQuery.toLowerCase();
    return queriesList.filter((q) => {
      const matchesSearch =
        !query ||
        [
          q.subject,
          q.description,
          q.student?.firstName,
          q.student?.lastName,
          q.student?.email,
          q.batch?.batchName,
          q.module?.title,
          q.lecture?.topic,
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query));

      const matchesAssigned =
        assignedToFilter === "all" ||
        (assignedToFilter === "unassigned" && !q.assignedTo) ||
        (assignedToFilter !== "unassigned" &&
          q.assignedTo?._id === assignedToFilter);

      return matchesSearch && matchesAssigned;
    });
  }, [queries, searchQuery, assignedToFilter]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "var(--color-error)";
      case "high":
        return "var(--color-warning)";
      case "medium":
        return "var(--color-primary)";
      case "low":
        return "var(--color-text-tertiary)";
      default:
        return "var(--color-text-secondary)";
    }
  };

  const columns = [
    {
      key: "subject",
      label: "Subject",
      render: (row) => (
        <div>
          <strong>{row.subject}</strong>
          <div className="query-meta">
            <span className="query-type">{row.queryType || "doubt"}</span>
            {row.module && (
              <span className="query-module">Module: {row.module.title}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "student",
      label: "Student",
      render: (row) => (
        <div>
          <strong>
            {row.student?.firstName} {row.student?.lastName}
          </strong>
          <span className="student-email">{row.student?.email}</span>
        </div>
      ),
    },
    {
      key: "batch",
      label: "Batch / Course",
      render: (row) => (
        <div>
          {row.batch?.batchName && (
            <div>
              <strong>{row.batch.batchName}</strong>
            </div>
          )}
          {row.batch?.course && (
            <span className="course-name">
              {typeof row.batch.course === "object"
                ? row.batch.course.title
                : "Course"}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status || "open"} />,
    },
    {
      key: "priority",
      label: "Priority",
      render: (row) => {
        const priority = row.priority || "medium";
        return (
          <span
            className={`priority-badge priority-${priority}`}
            style={{ color: getPriorityColor(priority) }}
          >
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </span>
        );
      },
    },
    {
      key: "assignedTo",
      label: "Assigned To",
      render: (row) => {
        if (!row.assignedTo)
          return <span className="text-muted">Unassigned</span>;
        return (
          <span>
            {row.assignedTo.firstName} {row.assignedTo.lastName}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row) => {
        if (!row.createdAt) return "—";
        return new Date(row.createdAt).toLocaleDateString();
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <Link to={`/queries/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ];

  const pagination = queries?.pagination || {};

  return (
    <div className="queries-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Query Management</p>
          <h1>All Queries</h1>
          <p className="subhead">Manage student queries and doubts</p>
        </div>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={loadQueries}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search queries by subject, student, batch..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Status" },
            { value: "open", label: "Open" },
            { value: "assigned", label: "Assigned" },
            { value: "in-progress", label: "In Progress" },
            { value: "resolved", label: "Resolved" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <Select
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Priority" },
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
            { value: "urgent", label: "Urgent" },
          ]}
        />
        <Select
          value={queryTypeFilter}
          onChange={(e) => {
            setQueryTypeFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Types" },
            { value: "doubt", label: "Doubt" },
            { value: "clarification", label: "Clarification" },
            { value: "technical", label: "Technical" },
            { value: "general", label: "General" },
            { value: "assignment", label: "Assignment" },
            { value: "exam", label: "Exam" },
          ]}
        />
        <Select
          value={batchFilter}
          onChange={(e) => {
            setBatchFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Batches" },
            ...batches.map((b) => ({
              value: b._id,
              label: b.batchName || `Batch ${b._id}`,
            })),
          ]}
        />
        <Select
          value={assignedToFilter}
          onChange={(e) => {
            setAssignedToFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Assignees" },
            { value: "unassigned", label: "Unassigned" },
            ...doubtSolvers.map((s) => ({
              value: s._id,
              label: `${s.firstName} ${s.lastName}`.trim() || s.email,
            })),
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredQueries}
        loading={isLoading}
        emptyMessage="No queries found"
      />

      {pagination.pages > 1 && (
        <div className="pagination">
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
          >
            Previous
          </Button>
          <span className="pagination-info">
            Page {pagination.page || page} of {pagination.pages || 1}
          </span>
          <Button
            variant="secondary"
            onClick={() =>
              setPage((p) => Math.min(pagination.pages || 1, p + 1))
            }
            disabled={page >= (pagination.pages || 1) || isLoading}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default QueriesList;
